import ApplicationIcon from '@components/Icon/Application';
import DeactivateIcon from '@components/Icon/Deactivate';
import JobIcon from '@components/Icon/Job';
import ReactivateIcon from '@components/Icon/Reactivate';
import UserIcon from '@components/Icon/User';
import TalentFloatingActionBar from '@modules/talents/components/TalentFloatingActionBar';
import TalentListGrid from '@modules/talents/TalentListGrid';
import TalentListPreview from '@modules/talents/TalentListPreview';
import TalentDataTable from '@modules/talents/TalentDataTable';
import { Box } from '@mui/material';
import { SortModel } from '@components/DataTable/types';


import { useTalentOperations } from './context/TalentOperationsContext';
import { buildTalentFavoritesMapDirect } from './utils/talentFavoritesMap';

type TalentScoringResult = {
    score?: number | null;
    status: 'pending' | 'in_progress' | 'done' | 'error';
    explainer?: string;
};

const cardMenuList = [
    {
        id: 0,
        path: '/',
        title: 'Profile Setting',
        style: 'white',
        type: 'redirect',
        icon: UserIcon,
    },
    {
        id: 1,
        path: '/talents/applications',
        title: 'Application',
        style: 'white',
        type: 'redirect',
        icon: ApplicationIcon,
    },
    {
        id: 2,
        path: '/',
        title: 'Suggest Job Offer',
        style: 'white',
        type: 'redirect',
        icon: JobIcon,
    },
    {
        id: 3,
        path: '/',
        title: 'Deactivate  Account',
        style: 'red',
        type: 'action',
        actionTitle: 'Are you sure you want to deactivate this Account?',
        actionDescription:
            'By deactivate this account, Talent  wont be able to access this account anymore. You can redo this action in the future',
        confirmTitle: 'Deactivate',
        cancelTitle: 'Annuler',
        action: 'deactivate',
        icon: DeactivateIcon,
        active: true,
    },
    {
        id: 4,
        path: '/',
        title: 'Reactivate Account',
        style: 'green',
        type: 'action',
        actionTitle: 'Are you sure you want to reactivate this Account?',
        actionDescription: 'By reactivate this account, Talent will be able to access this account again.',
        confirmTitle: 'Activate',
        cancelTitle: 'Annuler',
        action: 'activate',
        icon: ReactivateIcon,
        active: false,
    },
];

const headerColumnList = [
    {
        id: 0,
        title: 'Nom',
        // minWidth: '250px',
    },
    {
        id: 1,
        title: 'Évaluation',
    },
    {
        id: 2,
        title: 'Ville',
    },
    {
        id: 3,
        title: 'Expérience',
    },
    {
        id: 4,
        title: 'Contrat',
    },
    {
        id: 5,
        title: 'Étude',
    },
    {
        id: 6,
        title: 'Application',
    },
    {
        id: 7,
        title: 'Créé',
    },
    {
        id: 8,
        title: '',
    },
];

interface TalentTableOrganismsProps {
    viewMode: 'grid' | 'list' | 'preview';
    data: {
        _id: string;
        imageUrl: string;
        slug: string;
        fullName: string;
        jobOffer: string;
        brand: string;
        city: string;
        rating: number;
        origin: string;
        totalApplied: number;
        appliedJobs: number;
        experience: string;
        contractType: string;
        education: string;
        company: string;
        logo: string;
        active: boolean;
    }[];
    loading: boolean;
    scoringResultsByTalentId?: Record<string, TalentScoringResult>;
    showScoringColumn?: boolean;
    onOpenScoringDialog?: (talentId: string, data: { score?: number | null; status: TalentScoringResult['status']; explainer?: string }) => void;
    handleDownload: (url: string, label: string) => void;
    sortModel?: SortModel[];
    onSortChange?: (model: SortModel[]) => void;
    pagination?: {
        currentPage: number;
        pageSize: number;
        totalRows: number;
        onPageChange: (page: number) => void;
        onPageSizeChange: (pageSize: number) => void;
    };
    isLoadingMore?: boolean;
    onScrollEnd?: () => void;
    showFloatingActionBar?: boolean;
    onReuploadSuccess?: () => void;
}

const TalentTableOrganisms = ({
    viewMode,
    data,
    loading,
    scoringResultsByTalentId,
    showScoringColumn = false,
    onOpenScoringDialog,
    handleDownload,
    sortModel,
    onSortChange,
    pagination,
    isLoadingMore,
    onScrollEnd,
    showFloatingActionBar = true,
    onReuploadSuccess,
}: TalentTableOrganismsProps) => {
    const { selectedTalents, toggleTalent, currentStatus, updateRating, updateStatus, isUpdatingRating, isUpdatingStatus } = useTalentOperations();

    // Prepare talent favorites map for multi-selection
    const talentFavoritesMap = buildTalentFavoritesMapDirect(selectedTalents, data);


    if (viewMode === 'preview') return <TalentListPreview />;

    return (

            <Box sx={{ overflowX: 'auto', flex: 1, display: "flex", pb: 3 }} className="talent-table-organisms">
                {viewMode === 'list' && (
                    <TalentDataTable
                        cardMenuList={cardMenuList}
                        headerColumnList={headerColumnList}
                        data={data}
                        loading={loading}
                        scoringResultsByTalentId={scoringResultsByTalentId}
                        showScoringColumn={showScoringColumn}
                        onOpenScoringDialog={onOpenScoringDialog}
                        selectedTalents={selectedTalents}
                        sortModel={sortModel}
                        onSortModelChange={onSortChange}
                        pagination={pagination}
                        isLoadingMore={isLoadingMore}
                        onScrollEnd={onScrollEnd}
                        onReuploadSuccess={onReuploadSuccess}
                    />
                )}
                {viewMode === 'grid' && (
                    <TalentListGrid
                        cardMenuList={cardMenuList}
                        data={data}
                        loading={loading}
                        handleDownload={handleDownload}
                        updateRating={(id, rating) => updateRating([id], rating)}
                        updateStatus={(id, status) => updateStatus([id], status)}
                        selectable
                        selectedTalents={selectedTalents}
                        onSelectionChange={(talentId: string, selected: boolean, status?: string) => {
                            toggleTalent(talentId, status);
                        }}
                    />
                )}
                {showFloatingActionBar && selectedTalents.length > 0 && (
                    <TalentFloatingActionBar
                        selectedTalentIds={selectedTalents.map((talent) => talent._id)}
                        onSendMessage={() => console.log('Send message clicked', selectedTalents)}
                        onUpdateRating={(rating) => {
                            const talentIds = selectedTalents.map((talent) => talent._id);
                            updateRating(talentIds, rating);
                        }}
                        onStatusChange={(status) => {
                            const talentIds = selectedTalents.map((talent) => talent._id);
                            // Convert avatar status to backend status format
                            const backendStatus = status === 'nothing' ? '' : status;
                            updateStatus(talentIds, backendStatus);
                        }}
                        status={currentStatus}
                        isUpdatingRating={isUpdatingRating}
                        isUpdatingStatus={isUpdatingStatus}
                        talentFavoritesMap={talentFavoritesMap}
                    />
                )}
            </Box>

    );
};

export default TalentTableOrganisms;
